/**
 * The outgoing email log.
 *
 * Selection rides in a `?email=` search param rather than a route segment so
 * this view can be mounted anywhere — it now lives as a tab inside Settings
 * rather than owning a top-level nav item, and a `/emails/:id` route no longer
 * exists to supply a param.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { emailsApi, type SentEmail } from '../lib/emails-api';
import { leadloopApi } from '../lib/leadloop-api';
import { ActivityTimeline, DetailTabBody, NotesPanel, RelatedRecordList } from '../components/record-detail-panels';
import {
  CollectionViewToggle,
  CompactRecordList,
  CompactRecordListItem,
  DetailSection,
  RecordCard,
  RecordCardGrid,
  RecordDetailHeading,
  RecordDetailShell,
  RecordTable,
  RecordTableCell,
  RecordTablePrimaryCell,
  RecordTableRow,
  RecordTabs,
  WorkspaceSplitView,
  useCollectionView,
} from '@command-center/ui-system';
import { EmptyState, ErrorState, Field, LoadingState, StatusBadge, formatDateTime } from './parts';

export function EmailsView() {
  const [view, setView] = useCollectionView('cascade:records:emails:view');
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get('email');
  const emails = useQuery({ queryKey: ['emails', 'sent'], queryFn: () => emailsApi.list('sent') });

  const select = (nextId: string | null) => {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (nextId) params.set('email', nextId);
        else params.delete('email');
        return params;
      },
      { replace: true },
    );
  };

  if (emails.isLoading) return <LoadingState />;
  if (emails.isError) return <ErrorState error={emails.error} />;
  const items = emails.data?.items ?? [];
  if (items.length === 0) {
    return (
      <EmptyState label="This page will track outgoing emails sent from the portal. To start tracking incoming emails or setup an email address specifically for Lead management contact One Up." />
    );
  }
  const selectedIndex = id ? items.findIndex((email) => email.id === id) : -1;
  const selected = selectedIndex >= 0 ? items[selectedIndex] : undefined;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {!selected && (
        <div className="flex justify-end">
          <CollectionViewToggle view={view} onChange={setView} />
        </div>
      )}
      <WorkspaceSplitView
        detail={
          selected ? (
            <EmailDetailPanel
              email={selected}
              onClose={() => select(null)}
              onPrevious={selectedIndex > 0 ? () => select(items[selectedIndex - 1]!.id) : undefined}
              onNext={
                selectedIndex < items.length - 1
                  ? () => select(items[selectedIndex + 1]!.id)
                  : undefined
              }
            />
          ) : undefined
        }
      >
        {selected ? (
          <CompactRecordList title={`${items.length} emails`}>
            {items.map((email) => (
              <CompactRecordListItem
                key={email.id}
                selected={email.id === selected.id}
                title={email.subject}
                subtitle={email.to}
                meta={formatDateTime(email.sentAt ?? email.createdAt)}
                onSelect={() => select(email.id)}
              />
            ))}
          </CompactRecordList>
        ) : view === 'cards' ? (
          <RecordCardGrid>
            {items.map((email) => (
              <RecordCard
                key={email.id}
                recordType="email"
                icon={Mail}
                title={email.subject}
                subtitle={email.to}
                badges={<StatusBadge label={email.status} />}
                details={[
                  {
                    label: 'Recipient',
                    value: (
                      <a className="text-primary hover:underline" href={`mailto:${email.to}`}>
                        {email.to}
                      </a>
                    ),
                  },
                  { label: 'Sent', value: formatDateTime(email.sentAt ?? email.createdAt) },
                  { label: 'Linked lead', value: email.leadId ? 'Available' : undefined },
                ]}
                onOpen={() => select(email.id)}
                openLabel="Open email"
              />
            ))}
          </RecordCardGrid>
        ) : (
          <RecordTable
            minWidth={720}
            columns={[
              { key: 'subject', header: 'Subject' },
              { key: 'to', header: 'Recipient' },
              { key: 'sent', header: 'Sent' },
              { key: 'status', header: 'Status', align: 'right' },
            ]}
          >
            {items.map((email) => (
              <RecordTableRow key={email.id} onOpen={() => select(email.id)}>
                <RecordTablePrimaryCell title={email.subject} />
                <RecordTableCell>{email.to}</RecordTableCell>
                <RecordTableCell>{formatDateTime(email.sentAt ?? email.createdAt)}</RecordTableCell>
                <RecordTableCell align="right">
                  <StatusBadge label={email.status} />
                </RecordTableCell>
              </RecordTableRow>
            ))}
          </RecordTable>
        )}
      </WorkspaceSplitView>
    </div>
  );
}

function EmailDetailPanel({
  email,
  onClose,
  onPrevious,
  onNext,
}: {
  email: SentEmail;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
}) {
  const [tab, setTab] = useState('overview');
  useEffect(() => setTab('overview'), [email.id]);
  const activity = useQuery({
    queryKey: ['activity', email.id],
    queryFn: () => leadloopApi.activity({ entityId: email.id, limit: 100 }),
  });
  return (
    <RecordDetailShell recordType="email" onClose={onClose} onPrevious={onPrevious} onNext={onNext}>
      <RecordDetailHeading
        icon={Mail}
        title={email.subject}
        badges={<StatusBadge label={email.status} />}
      />
      <RecordTabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'message', label: 'Message' },
          { id: 'notes', label: 'Notes' },
          { id: 'activity', label: 'Activity' },
        ]}
      />
      <DetailTabBody>
        {tab === 'overview' && (
          <>
            <DetailSection title="Delivery details">
              <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                <Field
                  label="To"
                  value={
                    <a className="text-primary hover:underline" href={`mailto:${email.to}`}>
                      {email.to}
                    </a>
                  }
                />
                <Field label="Sent" value={formatDateTime(email.sentAt ?? email.createdAt)} />
                <Field label="Status" value={email.status} />
              </dl>
            </DetailSection>
            {email.leadId && (
              <DetailSection title="Linked lead">
                <RelatedRecordList
                  empty="No linked lead."
                  items={[{ id: email.leadId, to: `/leads/${email.leadId}`, title: 'Open linked lead' }]}
                />
              </DetailSection>
            )}
          </>
        )}
        {tab === 'message' && (
          <DetailSection title="Message">
            <pre className="whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-3 font-sans text-sm leading-relaxed text-foreground">
              {email.body}
            </pre>
          </DetailSection>
        )}
        {tab === 'notes' && (
          <NotesPanel
            entityType="email"
            entityId={email.id}
            entityLabel={email.subject}
            activity={activity.data?.items ?? []}
          />
        )}
        {tab === 'activity' && (
          <DetailSection title="Activity and history">
            <ActivityTimeline items={activity.data?.items ?? []} />
          </DetailSection>
        )}
      </DetailTabBody>
    </RecordDetailShell>
  );
}
